<script>
  // Currently, we don't start any queries based on changed input fields, except the search field on the map itself. Google Maps' search box does not support debouncing the API calls. Besides this, after the changes to Google Maps' pricing in May 2019 you do not pay per request, but rather per autocomplete session. It doesn't matter how many search queries the user types in, they all will still be counted as one session.
  import InputWithValidation from '../lib/components/InputField.svelte';
  import { onMount, onDestroy, createEventDispatcher } from 'svelte';
  import { exists, is_visible } from '../lib/utils';
  import Events from '../lib/events';
  import { MDCTextField } from '@material/textfield';
  import { TS } from '../config/translation-keys.js';
  import { t9n } from '../lib/translate.js';
  import CheckBox from '../lib/components/CheckBox.svelte';
  import NumberFieldWithButtons from '../lib/components/NumberFieldWithButtons.svelte';
  import session from '../lib/session';
  import settings from '../config';
  import { show_tutorial_screen } from '../lib/tutorials';

  // component options  ------------------------------------------------------ //


  export let input;
  export let is_address_valid = true;

  export let variant = 'outlined';
  export let page_state;
  export let page;
  export let is_first_time = false;
  export let wait_api;

  // internal variables ------------------------------------------------------ //

  let api_calls = 0;
  let map_search;
  let address;
  let country;
  let data;

  let country_name = '';
  let language = '';
  let current_pos;
  let current_location;
  let should_geocode_onload = true;
  let search_address = '';
  let map;
  let marker;
  let search_input;
  let map_element;
  let invalid_text;
  let textfield;
  let is_distance_valid = true;
  let distance_max;
  let allow_unlimited_distance;

  class GoogleError extends Error {}

  class LocationError extends Error {}

  let has_loaded;
  let loading_times = 0;
  // let info_window;

  // translated strings ------------------------------------------------------ //

  let labels = {
    unlimited_distance: null,
    distance: null,
    btn_back: null,
    btn_save: null,
    btn_next: null,
    invalid_str: null,
    invalid_str_name: null,
    invalid_ctry: null,
    invalid_post: null,
    invalid_addr_line: null,
    invalid_cord: null,
    no_res: null,
    geo_fail: null,
    choose_map: null,
    prm_addr: null,
    sec_addr: null,
    title_city: null,
    title_country: null,
    title_postal: null,
    btn_prev: null,
  };


  // utility functions -------------------------------------------------------- //

  const init_text_field = () => {
    textfield = new MDCTextField(
      document.getElementById('text_field_searchbox')
    );

    if (page === 'settings' && JSON.parse(window.localStorage.getItem('WP.tutorial')) && $session.user.last_role === 'worker') {
      show_tutorial_screen(true, 'tour_w3', 300, 'worker')
    }

    if (page === 'settings' && JSON.parse(window.localStorage.getItem('WP.tutorial')) && $session.user.last_role === 'orderer') {
      show_tutorial_screen(true, 'tour_w3', 300, 'orderer')
    }

    if (page !== 'settings' && JSON.parse(window.localStorage.getItem('WP.tutorial.new_order'))) {
      show_tutorial_screen(true, 'new_order_o6', 200, 'orderer');
    }
  };

  const sdispatch = createEventDispatcher();

  const fire = (name, detail) => {
    sdispatch(name, detail);
    Events.dispatch(name, detail);
  };

  const validate_distance = e => {
    is_distance_valid = input.distance > 1 && input.distance <= 60;
  }

  const decode_result = result => {
    let street_name;
    let street_number;
    let result_country;
    let result_country_code;
    let result_city;
    let province;
    let result_postal_code;
    let administrative_area;
    let administrative_area_2;
    let administrative_area_3;
    let address_component;

    let c_code;
    if(loading_times === 1) input.country_code = data.country.code;
    c_code = loading_times > 0 ? input.country_code : data.country.code;
    for (let i = 0; i < result.address_components.length; i++) {
      address_component = result.address_components[i];

      switch (address_component.types[0]) {
        case 'street_number':
          street_number = address_component.long_name;
          if (street_number === null || street_number === undefined) {
            is_address_valid = false;
            invalid_text = labels.invalid_str;
            throw new LocationError(labels.invalid_str, street_number);
          }
          break;
        case 'route':
          street_name = address_component.long_name;
          if (street_name === null || street_name === undefined) {
            is_address_valid = false;
            invalid_text = labels.invalid_str_name;
            throw new LocationError(labels.invalid_str_name, street_name);
          }
          break;
        case 'country':
          result_country = address_component.long_name;
          result_country_code = address_component.short_name;
          if (
            result_country_code !== c_code &&
            !$session.merchant.multi_country
          ) {
            is_address_valid = false;
            invalid_text = labels.invalid_ctry;
            throw new LocationError(labels.invalid_ctry, result_country);
          }
          break;
        case 'postal_city':
          result_city = address_component.long_name;
          break;
        case 'postal_town':
          result_city = address_component.long_name;
          break;
        case 'locality':
          if (!result_city) {
            result_city = address_component.long_name;
          }
          break;
        case 'postal_code':
          result_postal_code = address_component.long_name;
          if (result_postal_code === null || result_postal_code === undefined) {
            is_address_valid = false;
            invalid_text = labels.invalid_post;
            throw new LocationError(labels.invalid_post, result_postal_code);
          }
          break;
        case 'administrative_area_level_1':
          administrative_area = address_component.long_name;
          break;
        case 'administrative_area_level_2':
          administrative_area_2 = address_component.long_name;
          break;
        case 'administrative_area_level_3':
          administrative_area_3 = address_component.long_name;
          break;
        default:
          break;
      }
    }

    province = administrative_area;

    if (province === undefined) {
      province = administrative_area_2;
    }
    if (province === undefined) {
      province = administrative_area_3;
    }
    if (!result_city) {
      result_city = province;
    }
    if (result_city) {
      province = undefined;
    }
    if (!street_name) {
      invalid_text = labels.invalid_addr_line;
      throw new LocationError(labels.invalid_addr_line, {
        detail: { result: result },
      });
    }
    if (!result.geometry) {
      invalid_text = labels.invalid_cord;
      throw new LocationError(labels.invalid_cord, {
        detail: { result: result },
      });
    }

    let secondary_address;
    let city;
    let region;
    let postal_code;
    let longitude;
    let latitude;
    let country_code;


    if(loading_times > 0 || is_first_time || input.primary_address === null){
      input.primary_address = `${street_name ? street_name : ''} ${street_number ? street_number : ''}`;
      input.city = result_city;
      country_name = result_country;
      input.country_code = result_country_code;
      input.postal_code = result_postal_code;
      // region = province === undefined ? '' : province;
      input.latitude = result.geometry.location.lat;
      input.longitude = result.geometry.location.lng;
      current_pos = {
        lat: input.latitude,
        lng: input.longitude,
      };

      secondary_address = input.secondary_address;
      city = input.city;
      region = input.region;
      postal_code = input.postal_code;
      longitude = input.longitude;
      latitude = input.latitude;
      country_code = input.country_code;
    } else{

      secondary_address = data.address.secondary_address;
      city = data.address.city;
      region = data.address.region;
      postal_code = data.address.postal_code;
      longitude = data.position.longitude;
      latitude = data.position.latitude;
      country_code = data.country.code;
    }

    loading_times++;
    return {
      postal_code,
      street_name,
      street_number,
      secondary_address,
      city,
      region,
      country_name,
      country_code,
      longitude,
      latitude,
    };
  };

  const build_google_url = options => {
    let base_url = `https://maps.googleapis.com/maps/api/geocode/json?&key=${data.geocode_api_key}&language=${data.language}`;
    let search;

    if (options.address) {
      let postal_code = encodeURIComponent(options.address.postal_code);
      let primary_address = encodeURIComponent(options.address.primary_address);
      let city = encodeURIComponent(options.address.city);
      let country = encodeURIComponent(options.address.country);
      search = `address=${postal_code},${primary_address},${city},${country}`;
    } else if (options.position) {
      let lat = encodeURIComponent(options.position.lat);
      let lng = encodeURIComponent(options.position.lng);
      search = `latlng=${lat},+${lng}`;
    }

    return `${base_url}&${search}`;
  };

  const ask_google = async options => {
    let result;
    let url = build_google_url(options);
    let response = await fetch(url);
    let response_data = await response.json();

    if (response_data.status === 'OK' && response_data.results.length >= 1) {
      result = response_data.results[0];

      if (result) {
        return decode_result(result);
      } else {
        throw new GoogleError(labels.no_res, {
          detail: { status: status, results: response_data },
        });
      }
    } else {
      throw new GoogleError(labels.geo_fail, {
        detail: { status: status, results: response_data },
      });
    }
  };

  const handle_location_errors = error => {
    if (error.constructor === GoogleError) {
      console.debug(error);
      is_address_valid = false;
    } else if (error.constructor === LocationError) {
      console.debug(error);
      is_address_valid = false;
    } else {
      console.debug(error);
      is_address_valid = false;
    }
  };

  const address_from_position = () => {
    let options = {
      position: current_pos,
      country: country_name,
      language: language,
    };

    ask_google(options)
      .then(result => {
        map.setCenter(current_pos);
        is_address_valid = true;
        fire('locationchanged', result);
      })
      .catch(handle_location_errors);
  };

  const position_from_address = () => {
    let options = {
      address: {
        postal_code: input.postal_code,
        primary_address: input.primary_address,
        city: input.city,
        country: country_name,
      },
      language: language,
      componentRestrictions: {
        country: input.country_code,
      },
    };

    ask_google(options)
      .then(result => {
        map.setCenter(current_pos);
        map.setZoom(16);
        add_marker(current_pos);
        marker.setPosition(current_pos);
        is_address_valid = true;
        fire('locationchanged', result);
      })
      .catch(handle_location_errors);
  };

  const add_marker = () => {
    if (marker) {
      marker.setMap(null);
    }

    marker = new google.maps.Marker({
      position: current_pos,
      map: map,
      draggable: true,
    });

    marker.addListener('dragend', marker => {
      input.longitude = marker.latLng.lng();
      input.latitude = marker.latLng.lat();
      current_pos = { lat: input.latitude, lng: input.longitude };
      address_from_position(current_pos);
    });
  };

  const update_map = search_box => {
    let places = search_box.getPlaces();
    let bounds = new google.maps.LatLngBounds();

    if (places.length === 0) {
      return;
    }

    places.forEach(place => {
      if (!place.geometry) {
        console.log('Returned place contains no geometry');
        return;
      }

      input.latitude = place.geometry.location.lat();
      input.longitude = place.geometry.location.lng();
      current_pos = { lat: input.latitude, lng: input.longitude };
      search_address = '';

      address_from_position();

      marker.setPosition(place.geometry.location);

      if (place.geometry.viewport) {
        bounds.union(place.geometry.viewport);
      } else {
        bounds.extend(place.geometry.location);
      }
    });

    map.fitBounds(bounds);
  };

  const init_search = () => {
    let search_box = new google.maps.places.SearchBox(search_input);

    map.addListener('bounds_changed', function() {
      search_box.setBounds(map.getBounds());
    });

    search_box.addListener('places_changed', function() {
      update_map(search_box);
    });
  };

  const reset_map = data => {
    search_address = '';
    if(has_loaded || is_first_time || input.primary_address === null){
      input.primary_address = data.address.primary_address;
      input.secondary_address = data.address.secondary_address;
      input.city = data.address.city;
      input.region = data.address.region;
      input.postal_code = data.address.postal_code;
      input.distance = data.distance ? data.distance : 60;
      input.longitude = data.position.longitude;
      input.latitude = data.position.latitude;
      input.country_code = data.country.code;
    }


    country_name = data.country.name;
    language = $session.merchant.country_code.toLowerCase();
    current_pos = { lat: data.position.latitude, lng: data.position.longitude };
    is_address_valid = true;
    is_distance_valid = true;
  };

  const init_map = data => {
    reset_map(data);


    let country_code = has_loaded ? input.country_code : data.country.code;

    let map_appears = setInterval(() => {
      try {
        // if (exists(google) && is_visible(map_element)) {
        if (exists(google) && is_visible(map_element)) {
          clearInterval(map_appears);
          map = new google.maps.Map(map_element, {
            center: current_pos,
            zoom: 16,
            componentRestrictions: {
              country: country_code,
            },
          });

          map.addListener('resize', function() {
            setTimeout(function() {
              if (marker || marker.latLng) {
                map.setCenter(marker.latLng.lat(), marker.latLng.lng());
              } else {
                map.setCenter(current_pos);
              }
            }, 500);
          });

          add_marker();
          address_from_position();
          init_search();

          console.log('Google Maps loaded.');
        }
      } catch (e) {
        console.log('Connectig to Google Maps API...');
      }
    }, 250);
  };

  const load_location = () => {
    let script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?language=${data.language}&libraries=places&key=${data.maps_api_key}`;
    script.defer = true;
    document.body.append(script);

    init_map(data);
  };

  // reactive functions ------------------------------------------------------- //

  $: if (!!$session && !has_loaded) {
    distance_max = $session.merchant.distance.distance_max;
    allow_unlimited_distance =
      $session.merchant.distance.allow_unlimited_distance;
    data = {
      maps_api_key: settings.google_maps_api_key,
      places_api_key: settings.google_places_api_key,
      geocode_api_key: settings.google_geocode_api_key,
      language: $session.merchant.country_code.toLowerCase() || 'en',
      country: {
        name: $session.merchant.country_name,
        code: $session.merchant.country_code,
      },
      position: {
        latitude: input.latitude,
        longitude: input.longitude,
      },
      address: {
        primary_address: input.primary_address,
        secondary_address: input.secondary_address,
        city: input.city,
        region: input.region,
        postal_code: input.postal_code,
      },
      distance: input.distance,
    };
    current_pos = { lat: data.position.latitude, lng: data.position.longitude };


    load_location();
    has_loaded = true;
  }

  const translate_texts = () => {
    labels.autosave = t9n(TS.NEW_ORDER_AUTO_SAVE);
    labels.invalid_str = t9n(TS.NEW_ORDER_LOC_INVALID_STR);
    labels.invalid_str_name = t9n(TS.NEW_ORDER_LOC_INVALID_STR_NAME);
    labels.invalid_ctry = t9n(TS.NEW_ORDER_LOC_INV_CTRY);
    labels.invalid_post = t9n(TS.NEW_ORDER_LOC_POST);
    labels.invalid_addr_line = t9n(TS.NEW_ORDER_LOC_ADDR_LINE);
    labels.invalid_cord = t9n(TS.NEW_ORDER_LOC_INV_CORD);
    labels.no_res = t9n(TS.NEW_ORDER_LOC_NO_RES);
    labels.geo_fail = t9n(TS.NEW_ORDER_LOC_GEO_FAIL);
    labels.choose_map = t9n(TS.NEW_ORDER_LOC_CHOOSE_MAP);
    labels.prm_addr = t9n(TS.NEW_ORDER_LOC_PRM_ADDR);
    labels.sec_addr = t9n(TS.NEW_ORDER_LOC_SEC_ADDR);
    labels.title_city = t9n(TS.NEW_ORDER_LOC_CITY);
    labels.title_country = t9n(TS.NEW_ORDER_LOC_REGION);
    labels.title_postal = t9n(TS.NEW_ORDER_LOC_POSTAL);
    labels.unlimited_distance = t9n(TS.SETTINGS_LABEL_UNLIMITED_DIST);
    labels.distance = t9n(TS.SETTINGS_LABEL_DISTANCE);
    labels.btn_back = t9n(TS.TXT_BACK);
    labels.btn_save = t9n(TS.CHANGE_PASSWORD_SUBMIT_LABEL);
    labels.btn_next = t9n(TS.TABLE_BUTTON_NEXT);
    labels.btn_prev = t9n(TS.TABLE_BUTTON_PREV);
  };

  $: if (!!$session) {
    translate_texts();
  }

  const unload_map = () => {
    document
      .querySelectorAll('script[src^="https://maps.googleapis.com"]')
      .forEach(s => s.remove());

    document.head
      .querySelectorAll('script[src^="https://maps.googleapis.com"]')
      .forEach(s => s.remove());
  };

  onMount(init_text_field);
  onDestroy(unload_map);
</script>

<div class="mdc-layout-grid mobile-margin">
  <div class="mdc-layout-grid__inner">
    <div class="mdc-layout-grid__cell--span-7">

      <div data-tooltip={t9n(TS.TOOLTIPS_HOW_TO_USE_MAPS)} style=''>
        <div>
          <div
            id="text_field_searchbox"
            class:mdc-text-field--invalid={is_address_valid === false}
            class="search-input mdc-text-field mdc-text-field--outlined"
          >
            <input
              type="text"
              class="mdc-text-field__input"
              aria-labelledby="my-label-id"
              bind:this={search_input}
              id="search_address"
              name="search_address"
              bind:value={search_address} />
            <span  class="mdc-notched-outline" >
            <span class="mdc-notched-outline__leading" />
            <span data-tooltip={t9n(TS.TOOLTIPS_HOW_TO_USE_MAPS)} class="mdc-notched-outline__notch">
              <span class="mdc-floating-label" id="my-label-id">
                {labels.choose_map}
              </span>
            </span>
            <span class="mdc-notched-outline__trailing" />
          </span>
          </div>
        </div>
      </div>

      {#if is_address_valid === false}
        <div class="tooltip-error">
          <span class="tooltip-text tooltip-top">{invalid_text}</span>
        </div>
      {/if}

      <!--      <p />-->

      <div id="map" class="map" bind:this={map_element} />
    </div>

    <div class="mdc-layout-grid__cell--span-4" style="margin-left: -6px;">
      <!--            <div class="margin-top-8">-->

      <!--                <CheckBox id="virtual-address-checkbox"-->
      <!--                          bind:value={virtual_address}-->
      <!--                          label={labels.virtual_address}-->
      <!--                >-->
      <!--                </CheckBox>-->
      <!--            </div>-->

      {#if page === 'settings'}
        {#if $session.user.last_role === 'worker'}
          {#if allow_unlimited_distance === true}
            <div class="mdc-layout-grid" style="padding: 0;">
              <div class="mdc-layout-grid__inner">
                <div class="mdc-layout-grid__cell" style="margin-top: 15px;">
                  <CheckBox
                    id="virtual-address-checkbox"
                    width="100px"
                    bind:value={input.unlimited_distance}
                    label={labels.unlimited_distance} />
                </div>
                <div class="mdc-layout-grid__cell" style="margin-left: 55px;">
                  {#if !input.unlimited_distance}
                    <NumberFieldWithButtons
                      id="duration"
                      type="text"
                      unlimited_distance
                      label={labels.distance}
                      name="duration"
                      bind:value={input.distance}
                      width="150px"
                      is_minmax_val={true}
                      max_val={distance_max}
                      min_val={1} />
                  {:else}
                    <div style="width: 155px;">
                      <InputWithValidation
                        id="distance"
                        type="text"
                        label={labels.distance}
                        validate={false}
                        disabled={true}
                        width="150px" />
                    </div>
                  {/if}
                </div>
              </div>
            </div>
          {:else}
            <div data-tooltip={t9n(TS.TOOLTIPS_SETTINGS_DISTANCE)} style="">
              <NumberFieldWithButtons
                id="duration"
                type="text"
                label={labels.distance}
                name="duration"
                bind:value={input.distance}
                width="310px"
                is_minmax_val={true}
                max_val={distance_max}
                min_val={1} />
            </div>
          {/if}
        {/if}
        {#if $session.user.last_role === 'orderer'}
          <div class="margin-71" />
        {/if}
      {/if}

      {#if page !== 'settings'}
        <div class="margin-71" />
      {/if}

      <div style="margin-bottom: 0px">
        <InputWithValidation
          id="address-prim"
          {variant}
          type='text'
          margin_bottom={'1px'}
          label={labels.prm_addr}
          name="address-prim"
          disabled={true}
          hide_bg={false}
          bind:value={input.primary_address}
          width='310px' />
      </div>

      <div style="margin-bottom: 0px">
        <InputWithValidation
          id="address-sec"
          {variant}
          type='text'
          margin_bottom={'1px'}
          label={labels.sec_addr}
          name="address-sec"
          bind:value={input.secondary_address}
          width='310px' />
      </div>

      <div style="margin-bottom: 0px">
        <InputWithValidation
          id="city"
          {variant}
          type='text'
          margin_bottom={'1px'}
          label={labels.title_city}
          name="city"
          disabled={true}
          hide_bg={false}
          bind:value={input.city}
          width='310px' />
      </div>

      <div style="margin-bottom: 0px">
        <InputWithValidation
          id="country"
          {variant}
          type='text'
          margin_bottom={'1px'}
          label={labels.title_country}
          name="country"
          disabled={true}
          hide_bg={false}
          bind:value={input.region}
          width='310px' />
      </div>

      <div style="margin-bottom: 32px;">
        <InputWithValidation
          id="postal-code"
          {variant}
          type='text'
          margin_bottom={'1px'}
          label={labels.title_postal}
          name="postal-code"
          disabled={true}
          hide_bg={false}
          bind:value={input.postal_code}
          width='310px' />
      </div>
    </div>
  </div>

  <!-- Back + Next -->
  <p />

  {#if page === 'settings'}
    <div class='desktop-next-margin'
         style="display: flex; justify-content: flex-end; position: relative">
      <div class='mobile-next-margin-bottom' style="margin-right: 10px;">
        <button
          onclick="prev_tab()"
          class="mdc-button mdc-button--outlined margin-5">
          <span class="mdc-button__ripple" />
          {labels.btn_back}
        </button>
      </div>

      {#if !is_first_time && $session.user.last_role === 'worker'}
        <button
          onclick="next_tab()"
          data-cy="next"
          class="mdc-button mdc-button--unelevated margin-5">
          <span class="mdc-button__ripple" />
          {labels.btn_next}
        </button>
      {/if}

      {#if is_first_time && $session.user.last_role === 'worker'}
        <button
          onclick="update_profile()"
          class="mdc-button mdc-button--unelevated margin-5"
          disabled={wait_api}
          style="margin-left: 10px"
          data-cy="save">
          <span class="mdc-button__ripple" />
          {labels.btn_save}
        </button>
      {/if}

      {#if $session.user.last_role === 'orderer'}
        <button
          onclick="update_profile()"
          class="mdc-button mdc-button--unelevated margin-5"
          style="margin-left: 10px"
          disabled={wait_api}
          data-cy="save">
          <span class="mdc-button__ripple" />
          {labels.btn_save}
        </button>
      {/if}

    </div>
  {:else}
    <div class='desktop-next-margin' style="display: flex; justify-content: flex-end;">
      <div class='mobile-next-margin-bottom' style="margin-right: 10px;">
        <button
          onclick="prev_tab()"
          class="mdc-button mdc-button--outlined margin-5">
          <span class="mdc-button__ripple" />
          {labels.btn_prev}
        </button>
      </div>
      {#if is_address_valid === false}
        <button
          disabled
          id="next"
          class="mdc-button mdc-button--unelevated margin-5">
          <span class="mdc-button__ripple" />
          {labels.btn_next}
        </button>
      {:else}
        <button
          onclick="next_tab()"
          id="next"
          class="mdc-button mdc-button--unelevated margin-5">
          <span class="mdc-button__ripple" />
          {labels.btn_next}
        </button>
      {/if}
    </div>
  {/if}
</div>

<style>
    .changes-text {
        margin-top: 10px;
        margin-right: 10px;
        position: absolute;
        left: 2px;
        opacity: 0.5;
        font-size: 14px;
    }

    #map {
        width: 100%;
        height: 285px;
        background: grey;
        margin-top: 16px;
    }

    #map .gm-style-iw {
        background-color: #b00020 !important;
    }

    /*style the box*/
    .gm-style .gm-style-iw-c {
        background-color: #b00020 !important;
        top: 0 !important;
        left: 0 !important;
        width: 100% !important;
        height: 100% !important;
        min-height: 120px !important;
        padding-top: 10px;
        display: block !important;
    }

    /*style the p tag*/
    .gm-style .gm-style-iw p {
        padding: 10px;
        font-size: 12px;
    }

    /*style the arrow*/
    .gm-style {
        background-color: #b00020 !important;
        padding: 0;
        margin: 0;
        top: 0;
        color: #fff;
        font-size: 16px;
    }

    .tooltip-error {
        position: relative;
        font-size: 12px;
        top: -42px;
        animation: transition_in 0.6s;
    }

    .tooltip-error .tooltip-top {
        position: absolute;
        z-index: 1;
        bottom: 100%;
        right: 0;
        left: 50%;
    }

    .tooltip-error .tooltip-text {
        background-color: #b00020;
        color: #fff;
        text-align: center;
        border-radius: 6px;
        padding: 8px 8px;
    }

    .margin-71 {
        margin-top: 71px;
    }

    .tooltip-default .tooltip-text {
        background-color: var(--mdc-theme-secondary);
        color: #fff;
        text-align: center;
        border-radius: 6px;
        padding: 8px 8px;
        display: block;
        padding-left: 30px;
        padding-right: 30px;
        /*width: 437px;*/
        font-size: 13px;
        box-shadow: 1px 1px 3px 0px rgba(0, 0, 0, 0.3);
    }

    .tooltip-default .tooltip-text::after {
        content: ' ';
        position: relative;
        top: 30px;
        /*left: 50%;*/
        margin-left: -5px;
        border-width: 5px;
        border-style: solid;
        border-color: #018786 transparent transparent transparent;
    }

    /* Desktop Mode
        *******************************************************/
    @media (min-width: 1025px) {
        .search-input {
            width: 510px;
        }

        .desktop-next-margin {
            margin-right: -25px;
        }

        .mobile-next-margin-bottom {
            margin-bottom: 0;
        }
    }

    /* Tablet Mode
        *******************************************************/
    @media (min-width: 769px) and (max-width: 1024px) {
        .search-input {
            width: 100%;
        }
    }

    /* Tablet Mode
        *******************************************************/
    @media (min-width: 769px) and (max-width: 840px) {

        .margin-71 {
            margin-top: 0;
        }

        .mdc-layout-grid__cell--span-7 {
            grid-column-end: span 7;
        }
    }

    /* Mobile Mode
        *******************************************************/
    @media (min-width: 0) and (max-width: 768px) {
        .search-input {
            width: 100%;
        }

        .margin-71 {
            margin-top: 0;
        }

        .mobile-margin {
            margin-left: 50px;
            padding: 0;
        }

        .desktop-next-margin {
            margin-right: 0;
        }

        .mobile-next-margin-bottom {
            margin-bottom: 24px;
        }

        .mdc-layout-grid__cell--span-4 {
            grid-column-end: span 7;
        }

        .mdc-layout-grid__cell--span-7 {
            grid-column-end: span 7;
        }
    }
</style>